// Canonical data source for every FOTRIC acoustic imaging camera sold
// through ctrl.aero.  Used by:
//   • /products/fotric hub comparison table
//   • CompareCamerasModal on every individual product page
//
// Each entry lists the spec rows we surface in the comparison views; if a
// value is not published for a given model we use `—` (em-dash) so the
// column stays aligned.  Datasheet URLs are optional — if omitted, the UI
// shows a "Request Datasheet" affordance instead of a direct download.

// H-Series datasheet applies to both H4 and H6 variants.
const H_SERIES_DATASHEET =
  '/docs/ctrl-systems-h-series-datasheet.pdf';

export const ACOUSTIC_CAMERAS = [
  {
    id: 'h4mini',
    name: 'H4mini',
    tagline: 'Compact leak detection',
    productPage: '/products/acoustic-cameras/h4mini',
    datasheet: null,
    image:
      'https://static.wixstatic.com/media/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png/v1/fill/w_720,h_720,al_c,q_85,enc_auto/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png',
    bestFor: 'Portable, one-handed leak surveys',
    specs: {
      microphones: '112 MEMS',
      workingDistance: '0.3 – 100 m',
      frequencyRange: '2 – 100 kHz',
      fov: '66° × 52°',
      display: '3.5" IPS LCD (640 × 480)',
      digitalCamera: '13 MP industrial',
      detectionModes: 'Leak mode (level display)',
      partialDischarge: '—',
      leakEvaluation: '—',
      navipdm: '—',
      onDeviceReport: 'Acoustic image reports (on-device)',
      analysisSoftware: '—',
      battery: '3.6 V, 5000 mAh Li-ion',
      batteryRuntime: '≥ 4 h continuous',
      enclosure: 'IP54',
      weight: '770 g (1.70 lb)',
      dimensions: '243 × 95 × 150 mm',
      calibration: '—',
      warranty: '—',
      arrayRotation: 'Fixed',
    },
  },
  {
    id: 'h4',
    name: 'H4',
    tagline: 'H-Series dual-mode (compact)',
    productPage: '/products/acoustic-cameras/h-series',
    datasheet: H_SERIES_DATASHEET,
    image:
      'https://static.wixstatic.com/media/571718_587034860df449119866a3f97c96fcce~mv2.png/v1/fill/w_720,h_547,al_c,q_85,enc_auto/571718_587034860df449119866a3f97c96fcce~mv2.png',
    bestFor: 'Dual leak + PD inspections at a lower entry point',
    specs: {
      microphones: '112 MEMS',
      workingDistance: '0.3 – 200 m',
      frequencyRange: '2 – 100 kHz',
      fov: '66° × 52°',
      display: '5" Gorilla LCD (1280 × 720)',
      digitalCamera: '13 MP industrial',
      detectionModes: 'LQ (leak) + PD (PRPD)',
      partialDischarge: 'Auto: surface / floating / corona',
      leakEvaluation: 'Auto + annual cost',
      navipdm: '—',
      onDeviceReport: 'Word / PDF on-device',
      analysisSoftware: 'AnalyzIR',
      battery: '7.4 V, 3500 mAh Li-ion (2 packs)',
      batteryRuntime: '≥ 4 h per pack',
      enclosure: 'IP54',
      weight: '1.3 kg (2.86 lb)',
      dimensions: '175 × 151 × 95 mm',
      calibration: '2 years',
      warranty: '2 years',
      arrayRotation: 'Fixed',
    },
  },
  {
    id: 'h6',
    name: 'H6',
    tagline: 'H-Series flagship',
    productPage: '/products/acoustic-cameras/h-series',
    datasheet: H_SERIES_DATASHEET,
    image:
      'https://static.wixstatic.com/media/571718_587034860df449119866a3f97c96fcce~mv2.png/v1/fill/w_720,h_547,al_c,q_85,enc_auto/571718_587034860df449119866a3f97c96fcce~mv2.png',
    bestFor: 'High-sensitivity leak + PD inspections',
    specs: {
      microphones: '162 MEMS',
      workingDistance: '0.3 – 200 m',
      frequencyRange: '2 – 100 kHz',
      fov: '66° × 52°',
      display: '5" Gorilla LCD (1280 × 720)',
      digitalCamera: '13 MP industrial',
      detectionModes: 'LQ (leak) + PD (PRPD)',
      partialDischarge: 'Auto: surface / floating / corona',
      leakEvaluation: 'Auto + annual cost',
      navipdm: '—',
      onDeviceReport: 'Word / PDF on-device',
      analysisSoftware: 'AnalyzIR',
      battery: '7.4 V, 3500 mAh Li-ion (2 packs)',
      batteryRuntime: '≥ 4 h per pack',
      enclosure: 'IP54',
      weight: '1.3 kg (2.86 lb)',
      dimensions: '175 × 151 × 95 mm',
      calibration: '2 years',
      warranty: '2 years',
      arrayRotation: 'Fixed',
    },
  },
  {
    id: 'h-flex',
    name: 'H-Flex',
    tagline: '180° rotatable array',
    productPage: '/products/acoustic-cameras/h-flex',
    datasheet: null,
    image:
      'https://static.wixstatic.com/media/571718_b5167ab45dba4ec687063bb3e2d9a5cc~mv2.png/v1/fill/w_720,h_530,al_c,q_85,enc_auto/571718_b5167ab45dba4ec687063bb3e2d9a5cc~mv2.png',
    bestFor: 'Ergonomic overhead + ground-level inspections',
    specs: {
      microphones: '162 MEMS',
      workingDistance: '0.3 – 100 m',
      frequencyRange: '2 – 100 kHz',
      fov: '66° × 52°',
      display: '5" Gorilla LCD (1280 × 720)',
      digitalCamera: '13 MP industrial',
      detectionModes: 'LQ (leak) + PD (PRPD)',
      partialDischarge: 'Auto: surface / floating / corona',
      leakEvaluation: 'Auto + annual cost',
      navipdm: 'NaviPdM® AI Assistant',
      onDeviceReport: 'Word / PDF on-device',
      analysisSoftware: 'AnalyzIR',
      battery: '3.6 V, 9900 mAh Li-ion',
      batteryRuntime: '≥ 4 h continuous',
      enclosure: 'IP54',
      weight: '—',
      dimensions: '—',
      calibration: '—',
      warranty: '—',
      arrayRotation: '180° rotatable (stepless)',
    },
  },
  {
    id: 'h7',
    name: 'H7',
    tagline: 'Long-range flagship',
    productPage: '/products/acoustic-cameras/h7',
    datasheet: null,
    image:
      'https://static.wixstatic.com/media/0fa391_bab168322b7042a898cae58409a8922a~mv2.png/v1/fill/w_540,h_910,al_c,q_85,enc_auto/0fa391_bab168322b7042a898cae58409a8922a~mv2.png',
    bestFor: 'Long-range aviation + electrical inspections',
    specs: {
      microphones: '345 MEMS',
      workingDistance: '0.3 – 300 m',
      frequencyRange: '2 – 100 kHz',
      fov: '66° × 52°',
      display: '5" Gorilla LCD (1280 × 720)',
      digitalCamera: '13 MP industrial',
      detectionModes: 'LQ (leak) + PD (PRPD)',
      partialDischarge: 'AI auto-diagnosis + severity',
      leakEvaluation: 'AI auto + annual cost',
      navipdm: 'NaviPdM® Venus AI',
      onDeviceReport: 'Word / PDF on-device',
      analysisSoftware: 'AnalyzIR',
      battery: '7.4 V, 3500 mAh Li-ion (2 packs)',
      batteryRuntime: '≥ 6 h combined',
      enclosure: '—',
      weight: '—',
      dimensions: '—',
      calibration: '—',
      warranty: '—',
      arrayRotation: 'Fixed',
    },
  },
];

// Row definitions for the comparison table, grouped by category so both
// the hub table and the compare modal can render the same organized view.
export const COMPARE_ROW_GROUPS = [
  {
    label: 'Best For',
    rows: [{ key: 'bestFor', label: 'Best for', fromRoot: true }],
  },
  {
    label: 'Acoustic Sensor',
    rows: [
      { key: 'microphones', label: 'Microphone array' },
      { key: 'workingDistance', label: 'Working distance' },
      { key: 'frequencyRange', label: 'Frequency range' },
      { key: 'fov', label: 'Acoustic FOV' },
      { key: 'arrayRotation', label: 'Array rotation' },
    ],
  },
  {
    label: 'Detection & Diagnostics',
    rows: [
      { key: 'detectionModes', label: 'Detection modes' },
      { key: 'leakEvaluation', label: 'Leak cost evaluation' },
      { key: 'partialDischarge', label: 'Partial discharge diagnosis' },
      { key: 'navipdm', label: 'NaviPdM® AI assistant' },
    ],
  },
  {
    label: 'Camera & Display',
    rows: [
      { key: 'display', label: 'Display' },
      { key: 'digitalCamera', label: 'Digital camera' },
    ],
  },
  {
    label: 'Reporting & Software',
    rows: [
      { key: 'onDeviceReport', label: 'On-device reports' },
      { key: 'analysisSoftware', label: 'Analysis software' },
    ],
  },
  {
    label: 'Power & Ruggedness',
    rows: [
      { key: 'battery', label: 'Battery' },
      { key: 'batteryRuntime', label: 'Battery runtime' },
      { key: 'enclosure', label: 'Enclosure rating' },
    ],
  },
  {
    label: 'Physical',
    rows: [
      { key: 'weight', label: 'Weight' },
      { key: 'dimensions', label: 'Dimensions' },
    ],
  },
  {
    label: 'Service',
    rows: [
      { key: 'calibration', label: 'Recommended calibration' },
      { key: 'warranty', label: 'Warranty' },
    ],
  },
];

export const getCameraById = (id) => ACOUSTIC_CAMERAS.find((c) => c.id === id);

export const getCellValue = (camera, row) => {
  if (row.fromRoot) return camera[row.key];
  return camera.specs?.[row.key];
};

// Shared Boeing tool-coverage data for the acoustic imager family.
// Boeing tool tag COM-18702 = "Digital Camera - Acoustic".  FOTRIC
// cameras are marketed as form-fit-functional equivalents under airline
// tool-equivalency programs (FAA Part 145.109(c) / FAR 43.13(a)).
// Individual models each have their own equivalency status statement.
const BOEING_COM_18702_BASE = {
  toolTag: 'COM-18702',
  toolTagLabel: 'Boeing Common Tool',
  status: 'equivalent',
  boeingParts: [],
  tasks: [
    { number: '20-10-79-270-801', name: 'Ultrasonic Leak Detection Checks — pairs with SPL-1473 for cabin pressurization, A/C, bleed air, hydraulic bypass, parking brake, pitot-static, oxygen, and pressurized-component leaks (Boeing 737-600 AMM EGN, applicability EGN ALL)' },
    { number: 'General', name: 'Wide-area acoustic scanning where an adjustable-frequency (2 – 50 kHz) digital acoustic camera is called out by the AMM in lieu of a handheld probe' },
  ],
  regulatoryRefs: [
    'FAA 14 CFR 145.109(c)',
    'FAR 43.13(a)',
    'FAA Part 145',
  ],
};

export const getBoeingCoverage = (id) => {
  const perModel = {
    'h4mini': {
      summary:
        'The FOTRIC H4mini meets or exceeds the technical specification called out by Boeing tool tag COM-18702 for handheld acoustic imaging — 112 MEMS digital microphones, 2 – 100 kHz range (COM-18702 requires 2 – 50 kHz), and IP54 rugged form factor. Marketed as a form-fit-functional equivalent under airline tool-equivalency programs.',
      equivalencyStatement:
        'The FOTRIC H4mini is offered as an approved-equivalent alternative under airline tool-equivalency programs. It satisfies the acoustic sensing, frequency-range, and form-factor requirements referenced in Boeing tool tag COM-18702 for AMM ultrasonic leak-detection tasks. Adoption requires the operator\'s own Part 145 tool-equivalency documentation and quality-organization sign-off.',
    },
    'h4': {
      summary:
        'The FOTRIC H4 is one configuration of the H-Series platform that meets or exceeds the technical specification called out by Boeing tool tag COM-18702 for handheld acoustic imaging — 112 MEMS digital microphones, 2 – 100 kHz range, 5" Gorilla-Glass touchscreen, IP54.',
      equivalencyStatement:
        'The FOTRIC H4 is offered as an approved-equivalent alternative under airline tool-equivalency programs, satisfying the sensing, frequency-range, and rugged-enclosure requirements of Boeing tool tag COM-18702. Adoption requires the operator\'s own Part 145 tool-equivalency documentation.',
    },
    'h6': {
      summary:
        'The FOTRIC H6 is the flagship H-Series camera — 162 MEMS microphones, 2 – 100 kHz, 5" Gorilla-Glass touchscreen, IP54, 2-year calibration interval. Exceeds every technical requirement called out by Boeing tool tag COM-18702.',
      equivalencyStatement:
        'The FOTRIC H6 is offered as an approved-equivalent alternative under airline tool-equivalency programs. Its 162-mic array exceeds the microphone-density baseline in the COM-18702 specification, and its dual LQ (leak) + PD (partial discharge) modes cover both leak-detection and electrical-inspection use cases. Adoption requires the operator\'s own Part 145 tool-equivalency documentation.',
    },
    'h-flex': {
      summary:
        'The FOTRIC H-Flex adds a 180° rotatable sensor array to the 162-MEMS H-Series platform — approved-equivalent under Boeing tool tag COM-18702 with ergonomic and arc-flash-safety advantages for overhead and energized-gear inspections.',
      equivalencyStatement:
        'The FOTRIC H-Flex is offered as an approved-equivalent alternative under airline tool-equivalency programs for Boeing tool tag COM-18702. Its 180° rotatable array is a functional enhancement that supports arc-flash safe distance (>90 cm) on energized ground-support equipment while remaining within the COM-18702 sensing and frequency envelope. Adoption requires the operator\'s own Part 145 tool-equivalency documentation.',
    },
    'h7': {
      summary:
        'The FOTRIC H7 is the premium long-range H-Series flagship — 345 MEMS microphones (the highest density in a handheld acoustic camera), 0.3 – 300 m working distance, NaviPdM® Venus AI. Exceeds every technical requirement of Boeing tool tag COM-18702.',
      equivalencyStatement:
        'The FOTRIC H7 is offered as a premium approved-equivalent alternative under airline tool-equivalency programs for Boeing tool tag COM-18702. Its 345-mic array + 300 m working distance exceed the baseline specification for wide-area acoustic scanning, enabling long-distance inspection of tail-mounted APUs, hangar substation gear, and elevated GSE cabinets. Adoption requires the operator\'s own Part 145 tool-equivalency documentation.',
    },
  };
  const model = perModel[id];
  if (!model) return null;
  return { ...BOEING_COM_18702_BASE, ...model };
};
